import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { Router } from '@angular/router';
import { isEqual } from 'lodash-es';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, first, takeUntil, tap } from 'rxjs/operators';

import { OwnershipType, SubscriptionPlan } from '@vi-iot/client';

import { MatTooltip } from '@angular/material/tooltip';
import {
  AuthStore,
  BOOKING_TABS,
  BookingQueryParams,
  BookingService,
  BookingStore,
  DevicesStore,
  InstallationDetailsStore,
  InstallationPackageStatus,
  InstallationsStore,
  Platform,
} from '@store';
import { matTooltipSetter } from '../../../../shared/functions/mat-tooltip-setter/mat-tooltip-setter';

@Component({
  selector: 'app-booked-package-card',
  templateUrl: './booked-package-card.component.html',
  styleUrls: ['./booked-package-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BookedPackageCardComponent implements OnInit, OnDestroy {
  readonly BookingPlans = SubscriptionPlan;
  readonly platform = Platform;
  readonly OwnershipType = OwnershipType;

  @ViewChildren(MatTooltip)
  set matTooltip(queryList: QueryList<MatTooltip>) {
    matTooltipSetter(queryList);
  }

  installationPackage: InstallationPackageStatus;
  boilerSerial: string;
  gatewayPlatformType: Platform;
  isActiveSubscriptionsLoaded = false;
  shouldDisplayBadgeForResidential$: Observable<boolean>;
  deviceSubscriptionPlan: SubscriptionPlan;
  residentialBadgeOwnershipTypes = [
    OwnershipType.ResidentialEndUser,
    OwnershipType.Oma,
    OwnershipType.PreCommissioning,
  ];
  commercialBadgeOwnershipTypes = [OwnershipType.CommercialV1, OwnershipType.CommercialV2];

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public installationDetailsStore: InstallationDetailsStore,
    public bookingStore: BookingStore,
    public bookingService: BookingService,
    public installationsStore: InstallationsStore,
    public authStore: AuthStore,
    private devicesStore: DevicesStore,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.subscribeToPackedStatusLoadedState();
    this.fetchSubscriptionsIfInstallationListIsEmpty();
    this.devicesStore.selectedDevice$
      .pipe(
        filter((device) => device?.boilerSerial !== this.boilerSerial),
        tap((device) => {
          this.installationDetailsStore.getPermamentInspectionFeatureSupport(device.boilerSerial);
        }),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe((device) => {
        this.boilerSerial = device.boilerSerial;
        this.shouldDisplayBadgeForResidential$ = this.installationDetailsStore.shouldDisplayBookedPackageCardBadgeForResidential(
          this.boilerSerial
        );

        this.subscribeToInstallationSubscriptionDetails(this.boilerSerial);

        this.installationDetailsStore
          .selectInstallationSubscriptionPlan(this.boilerSerial)
          .pipe(
            takeUntil(this.ngUnsubscribe$),
            filter((subscriptionPlan) => !!subscriptionPlan)
          )
          .subscribe((subscriptionPlan) => {
            this.deviceSubscriptionPlan = subscriptionPlan;
            this.cdr.markForCheck();
          });
      });

    this.installationDetailsStore
      .getMainDevicePlatform()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((gatewayPlatformType) => {
        this.gatewayPlatformType = gatewayPlatformType;
      });
  }

  onUpgradeToPro(): void {
    this.bookingStore.selectPlan(SubscriptionPlan.Pro);
    const queryParams: BookingQueryParams = {
      selectedStep: BOOKING_TABS.CHECKOUT,
      boilerSerial: this.boilerSerial,
      update: true,
      platform: this.gatewayPlatformType,
    };

    this.navigateToBookingViewWithParams(queryParams);
    this.bookingStore.getSubscriptionsDetails(this.boilerSerial, true);
  }

  onBuyPackage(): void {
    const queryParams: BookingQueryParams = {
      boilerSerial: this.boilerSerial,
      platform: this.gatewayPlatformType,
    };

    this.navigateToBookingViewWithParams(queryParams);
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private navigateToBookingViewWithParams(queryParams: BookingQueryParams): void {
    this.router.navigate(['/booking'], {
      queryParams,
    });
  }

  private subscribeToInstallationSubscriptionDetails(boilerSerial: string): void {
    this.installationsStore
      .selectInstallationSubscriptionDetails(boilerSerial)
      .pipe(distinctUntilChanged(isEqual), takeUntil(this.ngUnsubscribe$))
      .subscribe((installationPackageStatus) => {
        if (installationPackageStatus?.plan === SubscriptionPlan.Free) {
          this.installationPackage = { ...installationPackageStatus, plan: SubscriptionPlan.Basic };
        } else {
          this.installationPackage = installationPackageStatus;
        }
        this.cdr.detectChanges();
      });
  }

  private fetchSubscriptionsIfInstallationListIsEmpty(): void {
    this.authStore.getCompanyIdForInstallationsListInit$
      .pipe(first(), takeUntil(this.ngUnsubscribe$))
      .subscribe((model) => {
        if (model.installationsCount === 0 && !model.isRsaUser && model.companyId) {
          this.bookingStore.getAllVitoguideSubscriptions(model.companyId);
        }
      });
  }

  private subscribeToPackedStatusLoadedState(): void {
    this.bookingStore.subscriptionsLoaded$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((isActiveSubscriptionsLoaded: boolean) => {
        this.isActiveSubscriptionsLoaded = isActiveSubscriptionsLoaded;
        this.cdr.detectChanges();
      });
  }

  onRenewSubscription(): void {
    let queryParams = {
      boilerSerial: this.boilerSerial,
      step: 3,
    };
    this.router.navigate(['/booking'], {
      queryParams,
    });
  }
}
